/**
 * Matter Manager Component
 * Two-column layout for managing Echo matters
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Card, Button, Input, Select, Stack, Flex, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { EditBillingRateModal } from './EditBillingRateModal';
import type { EchoMatter } from '../../api/types/echo';

// Matter from main database
export interface Matter {
  id: number;
  mattercode: string;
  name: string;
  description?: string;
}

export interface MatterManagerProps {
  availableMatters: Matter[];
  echoMatters: EchoMatter[];
  isLoadingAvailable?: boolean;
  isLoadingEcho?: boolean;
  onAddMatter: (matterId: number, billingRate: number) => Promise<void>;
  onUpdateBillingRate: (echoMatterId: number, newRate: number) => Promise<void>;
  onRemoveMatter?: (echoMatterId: number) => Promise<void>;
}

/**
 * Component for managing Echo matters with billing rates
 */
export function MatterManager({
  availableMatters,
  echoMatters,
  isLoadingAvailable = false,
  isLoadingEcho = false,
  onAddMatter,
  onUpdateBillingRate,
  onRemoveMatter,
}: MatterManagerProps) {
  const { theme } = useTheme();

  const [selectedMatterId, setSelectedMatterId] = useState<number | null>(null);
  const [billingRate, setBillingRate] = useState<string>('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const [editingMatter, setEditingMatter] = useState<EchoMatter | null>(null);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;

  // Filter out matters that are already in Echo
  const mattersNotInEcho = useMemo(() => {
    const echoMatterCodes = new Set(echoMatters.map((m) => m.mattercode));
    return availableMatters.filter((m) => !echoMatterCodes.has(m.mattercode));
  }, [availableMatters, echoMatters]);

  const handleAddMatter = useCallback(async () => {
    if (selectedMatterId === null) {
      setAddError('Please select a matter');
      return;
    }

    const rate = parseFloat(billingRate);
    if (isNaN(rate) || rate < 0) {
      setAddError('Please enter a valid billing rate');
      return;
    }

    setAddError(null);
    setIsAdding(true);

    try {
      await onAddMatter(selectedMatterId, rate);
      setSelectedMatterId(null);
      setBillingRate('');
    } catch (err) {
      setAddError(err instanceof Error ? err.message : 'Failed to add matter');
    } finally {
      setIsAdding(false);
    }
  }, [selectedMatterId, billingRate, onAddMatter]);

  const handleEditClick = useCallback((matter: EchoMatter) => {
    setEditingMatter(matter);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditingMatter(null);
  }, []);

  const handleEditSave = useCallback(async (matterId: number, newRate: number) => {
    await onUpdateBillingRate(matterId, newRate);
  }, [onUpdateBillingRate]);

  const matterOptions = useMemo(() => [
    { value: '', label: isLoadingAvailable ? 'Loading matters...' : 'Select a matter' },
    ...mattersNotInEcho.map((m) => ({
      value: String(m.id),
      label: `${m.mattercode} - ${m.name}`,
    })),
  ], [mattersNotInEcho, isLoadingAvailable]);

  return (
    <>
      <Flex gap="lg" align="start" style={{ flexWrap: 'wrap' }}>
        {/* Left Column: Add New Matter */}
        <Card variant="elevated" style={{ flex: '1', minWidth: '300px' }}>
          <Card.Body>
            <Stack gap="md">
              <h3 style={{ margin: 0, color: textColor }}>Add Matter to Echo</h3>
              <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm }}>
                Select a matter from the main database and assign a billing rate.
              </p>

              {/* Matter Select */}
              <Stack gap="xs">
                <label style={{ ...textStyles.label, color: textColor }}>
                  Matter
                </label>
                <Select
                  value={selectedMatterId !== null ? String(selectedMatterId) : ''}
                  onChange={(value: string) => {
                    setSelectedMatterId(value ? parseInt(value, 10) : null);
                  }}
                  options={matterOptions}
                  disabled={isLoadingAvailable || isAdding}
                />
              </Stack>

              {/* Billing Rate Input */}
              <Stack gap="xs">
                <label style={{ ...textStyles.label, color: textColor }}>
                  Billing Rate ($/min)
                </label>
                <Flex align="center" gap="sm">
                  <span style={{ color: mutedColor }}>$</span>
                  <Input
                    type="number"
                    value={billingRate}
                    onChange={(e) => setBillingRate(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    disabled={isAdding}
                    style={{ flex: 1 }}
                  />
                  <span style={{ color: mutedColor }}>/min</span>
                </Flex>
              </Stack>

              {addError && (
                <span style={{ fontSize: fontSize.xs, color: errorColor }}>{addError}</span>
              )}

              <Button
                variant="solid"
                color="primary"
                onClick={handleAddMatter}
                disabled={isAdding || selectedMatterId === null}
              >
                {isAdding ? 'Adding...' : 'Add Matter'}
              </Button>

              {mattersNotInEcho.length === 0 && !isLoadingAvailable && (
                <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm, fontStyle: 'italic' }}>
                  All available matters have been added to Echo.
                </p>
              )}
            </Stack>
          </Card.Body>
        </Card>

        {/* Right Column: Current Echo Matters */}
        <Card variant="elevated" style={{ flex: '2', minWidth: '400px' }}>
          <Card.Body>
            <Stack gap="md">
              <Flex justify="between" align="center">
                <h3 style={{ margin: 0, color: textColor }}>Current Echo Matters</h3>
                <span style={{ fontSize: fontSize.sm, color: mutedColor }}>
                  {echoMatters.length} {echoMatters.length === 1 ? 'matter' : 'matters'}
                </span>
              </Flex>

              {isLoadingEcho ? (
                <p style={{ color: mutedColor }}>Loading matters...</p>
              ) : echoMatters.length === 0 ? (
                <p style={{ color: mutedColor, fontStyle: 'italic' }}>
                  No matters added to Echo yet.
                </p>
              ) : (
                <div
                  style={{
                    border: `1px solid ${theme.colors.border.default}`,
                    borderRadius: '6px',
                    overflow: 'hidden',
                  }}
                >
                  {/* Table Header */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 150px 120px 100px',
                      padding: `${spacing.sm} ${spacing.md}`,
                      background: theme.colors.background.paper,
                      borderBottom: `1px solid ${theme.colors.border.default}`,
                      fontSize: fontSize.xs,
                      fontWeight: 600,
                      color: mutedColor,
                      textTransform: 'uppercase',
                    }}
                  >
                    <div>Matter</div>
                    <div>Client</div>
                    <div>Rate</div>
                    <div>Actions</div>
                  </div>

                  {/* Table Rows */}
                  {echoMatters.map((matter) => (
                    <div
                      key={matter.id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 150px 120px 100px',
                        padding: `${spacing.sm} ${spacing.md}`,
                        borderBottom: `1px solid ${theme.colors.border.default}`,
                        alignItems: 'center',
                        fontSize: fontSize.sm,
                      }}
                    >
                      <div style={{ fontWeight: 500, color: textColor }}>
                        {matter.mattercode}
                      </div>
                      <div style={{ color: mutedColor, fontSize: fontSize.sm }}>
                        {matter.name}
                      </div>
                      <div style={{ color: textColor }}>
                        ${(matter.billing_rate ?? 0).toFixed(2)}/min
                      </div>
                      <div>
                        <Button
                          variant="outline"
                          color="primary"
                          size="sm"
                          onClick={() => handleEditClick(matter)}
                        >
                          Edit
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Stack>
          </Card.Body>
        </Card>
      </Flex>

      {/* Edit Modal */}
      <EditBillingRateModal
        isOpen={!!editingMatter}
        onClose={handleEditClose}
        onSave={handleEditSave}
        matter={editingMatter}
      />
    </>
  );
}

export default MatterManager;
